import React, { useEffect, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import { Link } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import {
  TableRow,
  TableCell,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";

import { formatPrice } from "utils/priceformatter";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";

import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
} from "utils/colors";
import CommonChip from "Components/UI/Tables/CommonChip";
import { useEndCustomerPaid } from "Context/EndCustomerPaid";
import useTableOptions from "Hooks/useTableOptions";
import CommentColumn from "./CommentColumn";
import ConfirmModal from "./ConfirmModal";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const InvoiceFilterType = {
  NONE: 0,
  NOT_PLACED: 1,
  DONE: 2,
};

const createTableRows = (invoices) => {
  return [
    ...invoices?.map((i) => {
      const connectedInvoices = i.InvoiceMatches.filter(
        (invoice) => invoice.Connected
      );
      return [
        i.Id,
        i.Id,
        i.FileId,
        i.OCR,
        i.EndCustomerName,
        i.Amount ? i.Amount : 0,
        {
          value: i.AmountLeft ? i.AmountLeft : 0,
          invoices: connectedInvoices,
        },
        i.Comment,
        getParseStatusLabelById(i.ParseStatus),
        cutStrAtAndAddDots(i.ParseMessage, 45),
      ];
    }),
  ];
};

export default function EndCustomerPaidMatchingTable() {
  const {
    invoices,
    selectedInvoice,
    setSelectedInvoiceId,
    matchConnectTwoPayments,
    loading,
  } = useEndCustomerPaid();
  const [rows, setRows] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState(InvoiceFilterType.NONE);
  const { openSuccess, openError } = usePopupNotificationContext();

  const [connectCreditMode, setConnectCreditMode] = useState(false);
  const [selectedCreditId, setSelectedCreditId] = useState(null);
  const [selectedInvoiceIdCreditMode, setSelectedInvoiceIdCreditMode] =
    useState(null);

  const { handleColumnShow, isColDisplayed } = useTableOptions(
    "EndCustomerPaidsMatchingTable"
  );

  useEffect(() => {
    setSelectedCreditId(null);
    setSelectedInvoiceIdCreditMode(null);
  }, [connectCreditMode]);

  useEffect(() => {
    const tmpRows = createTableRows(invoices);
    setRows(tmpRows);
  }, [invoices]);

  const handleConfirm = async () => {
    const res = await matchConnectTwoPayments(
      selectedInvoiceIdCreditMode,
      selectedCreditId
    );

    if (res.data && res.data.Status.toLowerCase() === "ok") {
      openSuccess("Ok!", 3000);
      setConnectCreditMode(false);
    } else {
      openError(res.data.Message, 3000);
    }

    setSelectedCreditId(null);
    setSelectedInvoiceIdCreditMode(null);
  };

  const onCopyClick = (e, value) => {
    e.stopPropagation();
    navigator.clipboard
      .writeText(value)
      .then(() => {
        openSuccess("Kopierat!", 3000);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const columns = [
    {
      name: "",
      options: {
        filter: false,
        sort: false,
        display: isColDisplayed(""),
        customBodyRender: (value) => {
          return (
            <IconButton
              component={Link}
              to={`/endcustomerpaid/${value}`}
              aria-label="edit"
              size="small"
            >
              <LinkIcon sx={{ p: 0, m: 0 }} />
            </IconButton>
          );
        },
      },
    },
    {
      name: "Id",
      label: "ID",
      options: {
        display: isColDisplayed("Id"),
        filter: false,
      },
    },
    {
      name: "FileId",
      label: "Fil ID",
      options: {
        empty: false,
        display: isColDisplayed("FileId"),
        customBodyRender: (value, tableMeta, updateValue) => {
          return <Link to={`/files/${value}`}>{value}</Link>;
        },
      },
    },
    {
      name: "Referens",
      label: "Referens",
      options: {
        display: isColDisplayed("Referens"),
        filter: false,
        customBodyRender: (value) => {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {value}
              <ContentCopyIcon
                sx={{
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "rgba(0, 0, 0, 0.5)",
                }}
                onClick={(e) => onCopyClick(e, value)}
              />
            </div>
          );
        },
      },
    },
    {
      name: "EndCustomerName",
      label: "Namn",
      options: {
        display: isColDisplayed("EndCustomerName"),
        filter: false,
      },
    },
    {
      name: "Amount",
      label: "Ursprungligt belopp",
      options: {
        display: isColDisplayed("Amount"),
        filter: false,
        customBodyRender: (value) => (
          <div style={{ textAlign: "right" }}>{formatPrice(value)}</div>
        ),
      },
    },
    {
      name: "AmountLeft",
      label: "Kvar att koppla",
      options: {
        filter: false,
        display: isColDisplayed("AmountLeft"),
        filterList: [invoiceFilter],
        customFilterListOptions: {
          render: (value) => {
            switch (value) {
              case InvoiceFilterType.NONE:
                return "Visa alla";
              case InvoiceFilterType.NOT_PLACED:
                return "Visa okopplade";
              case InvoiceFilterType.DONE:
                return "Visa färdiga";
              default:
                return;
            }
          },
        },
        filterOptions: {
          logic(tags, filters) {
            const number = Number(tags.value);
            switch (filters[0]) {
              case InvoiceFilterType.NONE:
                return false;
              case InvoiceFilterType.NOT_PLACED:
                return number === 0;
              case InvoiceFilterType.DONE:
                return number !== 0;
              default:
                return false;
            }
          },
        },
        customBodyRender: (value) => {
          let isOrange = false;
          const number = value.value;
          if (Number(number) === 0) {
            for (const invoice of value.invoices) {
              if (Number(invoice.RemainingAmount) !== 0) {
                isOrange = true;
              }
            }
          }

          return (
            <Chip
              label={formatPrice(value.value)}
              sx={{
                background:
                  number > 0
                    ? "black"
                    : number < 0
                    ? "#d32f2f"
                    : isOrange
                    ? "#ed6c02"
                    : "#2e7d32",
                color: "white",
                minWidth: "100px",
              }}
            />
          );
        },
      },
    },
    {
      name: "OtherRef",
      label: "Övrig ref",
      options: {
        display: isColDisplayed("OtherRef"),
        customBodyRender: (value) => {
          const shortStr = value?.slice(0, 15);
          const valueStr = shortStr ? `${shortStr}...` : "";
          return <CommentColumn value={value} shortValue={valueStr} />;
        },
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        filter: true,
        sort: true,
        display: isColDisplayed("Kontrollstatus"),
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "ParseMessage",
      label: "Kontrollmedelande",
      options: {
        display: isColDisplayed("ParseMessage"),
        filter: false,
      },
    },
  ];

  const checkColumnDisplay = (colIndex) => {
    const col = columns[colIndex];
    if (col) {
      return isColDisplayed(col.name);
    }
  };

  const handleRowClick = (id) => {
    if (!connectCreditMode) {
      setSelectedInvoiceId(id);
      return;
    }

    if (!selectedCreditId) {
      setSelectedCreditId(id);
      return;
    }

    if (selectedCreditId === id) {
      setSelectedCreditId(null);
      return;
    }

    const firstSelection = invoices?.find((i) => i.Id === selectedCreditId);
    const secondSelection = invoices?.find((i) => i.Id === id);
    if (
      (firstSelection &&
        secondSelection &&
        firstSelection.Amount >= 0 &&
        secondSelection.Amount >= 0) ||
      (firstSelection.Amount < 0 && secondSelection.Amount < 0)
    ) {
      openError("Valda betalningar kan inte matchas.");
      setSelectedCreditId(null);
      setSelectedInvoiceIdCreditMode(null);
    } else {
      setSelectedInvoiceIdCreditMode(id);
    }
  };

  const options = {
    rowsPerPage: 1000,
    rowsPerPageOptions: [10, 30, 100, 1000],
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: true,
    tableBodyMaxHeight: "70vh",
    customToolbar: () => {
      return (
        <div style={{ margin: "10px" }}>
          <Button
            variant="contained"
            onClick={() => setConnectCreditMode(!connectCreditMode)}
          >
            {`Justera kredit: ${connectCreditMode ? "På" : "Av"}`}
          </Button>
        </div>
      );
    },
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    customRowRender: (data, _, rowIndex) => {
      return (
        <TableRow
          style={{
            background:
              selectedInvoice?.Id === data[1] ||
              selectedCreditId === data[1] ||
              selectedInvoiceIdCreditMode === data[1]
                ? "rgba(25, 118, 210, 0.08)"
                : null,
          }}
          key={rowIndex}
          onClick={(event) => {
            event.stopPropagation();
            handleRowClick(data[1]);
          }}
        >
          {data.map((cell, cellIndex) => {
            if (checkColumnDisplay(cellIndex)) {
              return <TableCell key={cellIndex}>{cell}</TableCell>;
            }
          })}
        </TableRow>
      );
    },
    textLabels: {
      body: {
        noMatch: loading ? <CircularProgress /> : "",
      },
    },
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const csvData = data.map(({ data, index }) => {
        const remainingAmountLeftIndex = columns.findIndex(
          (col) => col.name === "AmountLeft"
        );

        const rowData = data.map((r1, index) => {
          if (index === remainingAmountLeftIndex) {
            return r1.value;
          }
          return r1;
        });

        return { index: index, data: rowData };
      });

      return "\uFEFF" + buildHead(columns) + buildBody(csvData);
    },
  };
  return (
    <>
      <div style={{ width: "100%" }}>
        <MUITable
          options={options}
          title={
            <>
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                disabled={invoiceFilter === InvoiceFilterType.NONE}
                onClick={() => setInvoiceFilter(InvoiceFilterType.NONE)}
              >
                Visa alla
              </Button>
              <Button
                sx={{ marginRight: 2 }}
                variant="contained"
                disabled={invoiceFilter === InvoiceFilterType.NOT_PLACED}
                onClick={() => setInvoiceFilter(InvoiceFilterType.NOT_PLACED)}
              >
                Visa kvar att koppla
              </Button>
              <Button
                variant="contained"
                disabled={invoiceFilter === InvoiceFilterType.DONE}
                onClick={() => setInvoiceFilter(InvoiceFilterType.DONE)}
              >
                Visa färdiga
              </Button>
            </>
          }
          data={rows}
          columns={columns}
        />
        <ConfirmModal
          open={selectedCreditId && selectedInvoiceIdCreditMode}
          onClose={() => {
            setSelectedCreditId(null);
            setSelectedInvoiceIdCreditMode(null);
            setConnectCreditMode(false);
          }}
          onConfirm={handleConfirm}
          credit={invoices.find((inv) => inv.Id === selectedCreditId)}
          invoice={invoices.find(
            (inv) => inv.Id === selectedInvoiceIdCreditMode
          )}
        />
      </div>
    </>
  );
}
